// The survey page's sections (redesign spec §6): General and Appearance
// today, steps/placements/danger zone arrive in Tasks 10-13. `SECTION` is
// the one place every section id is spelled out — the nav, the preview
// target mapping (survey-preview-target.ts) and each section's own
// `SectionCard` all read off it, so a rename only ever touches this file.

import { Badge, Button, PropertyPanel, toast } from "@pej/kore/components";
import { Pencil } from "lucide-react";
import { type ReactNode, useEffect } from "react";
import { ApiError } from "@/api/error";
import { type PatchSurvey, usePatchSurvey } from "@/api/surveys";
import { useTemplates } from "@/api/templates";
import type { Survey } from "@/api/types";
import { toastFormError } from "@/components/error-toast";
import { useRetry } from "@/components/page-states";
import { SectionCard } from "@/components/section-page/section-card";
import { useSectionEdit } from "@/components/section-page/section-edit";
import { Form, useKoreForm } from "@/lib/forms";
import { languageLabel } from "./languages";
import {
    AppearanceFields,
    GeneralFields,
    HeaderFields,
    type SurveyDetailsValues,
    surveyDetailsSchema,
    ThankYouFields,
    toDetailsDefaults,
} from "./survey-details-fields";

export const SECTION = {
    general: "section-general",
    appearance: "section-appearance",
    header: "section-header",
    steps: "section-steps",
    step: (id: string) => `section-step-${id}`,
    newStep: "section-step-new",
    placements: "section-placements",
    placement: (id: string) => `section-placement-${id}`,
    newPlacement: "section-placement-new",
    thanks: "section-thanks",
    danger: "section-danger",
    history: "section-history",
} as const;

function DetailsSection({
    survey,
    sectionId,
    title,
    pick,
    fields,
    view,
    onLive,
}: {
    survey: Survey;
    sectionId: string;
    title: string;
    /** The PATCH body for this section's own fields. */
    pick: (values: SurveyDetailsValues) => PatchSurvey;
    fields: ReactNode;
    view: ReactNode;
    onLive: (values: SurveyDetailsValues | null) => void;
}) {
    const patchSurvey = usePatchSurvey(survey.workspaceId, survey.id);
    const defaults = toDetailsDefaults(survey);
    const form = useKoreForm({ schema: surveyDetailsSchema, defaultValues: defaults });
    const edit = useSectionEdit({
        sectionId,
        sectionTitle: title,
        isDirty: form.formState.isDirty,
        hasErrors: Object.keys(form.formState.errors).length > 0,
        reset: () => form.reset(toDetailsDefaults(survey)),
    });

    // Saved data moved on (this section's save, or another's) — follow it
    // while not editing, so the next Edit starts from what is stored.
    const savedKey = JSON.stringify(defaults);
    // biome-ignore lint/correctness/useExhaustiveDependencies: keyed on the saved values themselves
    useEffect(() => {
        if (!edit.isEditing) form.reset(toDetailsDefaults(survey));
    }, [savedKey, edit.isEditing]);

    // Live values go out only while editing, and the edit's end clears them in
    // the cleanup: React runs every cleanup before any effect body, so a
    // section leaving edit never overwrites the values of the section that
    // took over, and a section that isn't editing never clears anyone's.
    useEffect(() => {
        if (!edit.isEditing) return;
        onLive(form.getValues());
        const subscription = form.watch((value) => onLive(value as SurveyDetailsValues));
        return () => {
            subscription.unsubscribe();
            onLive(null);
        };
    }, [edit.isEditing, form, onLive]);

    async function onSubmit(values: SurveyDetailsValues) {
        try {
            await patchSurvey.mutateAsync(pick(values));
            toast.success(`${title} saved`);
            edit.exitEdit();
        } catch (err) {
            if (err instanceof ApiError) {
                toastFormError("Save failed", form, err);
                return;
            }
            throw err;
        }
    }

    const actions = edit.isEditing ? (
        <div className="flex gap-2">
            <Button type="button" variant="ghost" size="sm" onClick={edit.exitEdit}>
                Cancel
            </Button>
            <Button
                type="button"
                size="sm"
                disabled={!form.formState.isDirty || patchSurvey.isPending}
                onClick={() => void form.handleSubmit(onSubmit)()}
            >
                Save
            </Button>
        </div>
    ) : edit.isReadOnly ? null : (
        <Button type="button" variant="outline" size="sm" onClick={edit.enterEdit}>
            <Pencil aria-hidden="true" />
            Edit
        </Button>
    );

    return (
        <SectionCard id={sectionId} title={title} actions={actions}>
            {edit.isEditing ? (
                <Form form={form} onSubmit={onSubmit}>
                    {fields}
                </Form>
            ) : (
                view
            )}
        </SectionCard>
    );
}

export function GeneralSection({
    survey,
    onLive,
}: {
    survey: Survey;
    onLive: (values: SurveyDetailsValues | null) => void;
}) {
    return (
        <DetailsSection
            survey={survey}
            sectionId={SECTION.general}
            title="General"
            pick={(v) => ({ name: v.name, active: v.active, language: v.language })}
            fields={<GeneralFields showStatus />}
            view={
                <PropertyPanel>
                    <PropertyPanel.Field label="Name">{survey.name}</PropertyPanel.Field>
                    <PropertyPanel.Field
                        label="Status"
                        help="An inactive survey's placements stop accepting responses."
                    >
                        <Badge tone={survey.active ? "success" : "neutral"}>
                            {survey.active ? "Active" : "Inactive"}
                        </Badge>
                    </PropertyPanel.Field>
                    <PropertyPanel.Field
                        label="Language"
                        help="Buttons and system messages. Your own wording above is never translated."
                    >
                        {languageLabel(survey.language)}
                    </PropertyPanel.Field>
                </PropertyPanel>
            }
            onLive={onLive}
        />
    );
}

export function AppearanceSection({
    survey,
    onLive,
}: {
    survey: Survey;
    onLive: (values: SurveyDetailsValues | null) => void;
}) {
    const templatesQuery = useTemplates(survey.workspaceId);
    const templatesRetry = useRetry(templatesQuery);
    // "No template" only when the survey has none: while the templates load
    // or after they fail, its name isn't known, which is not the same thing.
    const templateName = !survey.templateId ? (
        "No template"
    ) : templatesRetry.failed ? (
        <span className="flex items-center gap-2">
            <span className="text-kore-text-muted">Couldn't load the template</span>
            <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={templatesRetry.props.retrying}
                onClick={templatesRetry.props.onRetry}
            >
                {templatesRetry.props.retrying ? "Trying again…" : "Try again"}
            </Button>
        </span>
    ) : !templatesQuery.data ? (
        <span className="text-kore-text-muted">…</span>
    ) : (
        (templatesQuery.data.find((template) => template.id === survey.templateId)?.name ?? "No template")
    );
    return (
        <DetailsSection
            survey={survey}
            sectionId={SECTION.appearance}
            title="Appearance"
            pick={(v) => ({ templateId: v.templateId || null })}
            fields={<AppearanceFields workspaceId={survey.workspaceId} />}
            view={
                <PropertyPanel>
                    <PropertyPanel.Field label="Template" help="The shared look this survey renders under.">
                        {templateName}
                    </PropertyPanel.Field>
                </PropertyPanel>
            }
            onLive={onLive}
        />
    );
}

export function HeaderSection({
    survey,
    onLive,
}: {
    survey: Survey;
    onLive: (values: SurveyDetailsValues | null) => void;
}) {
    return (
        <DetailsSection
            survey={survey}
            sectionId={SECTION.header}
            title="Header"
            pick={(v) => ({
                displayTitle: v.displayTitle.trim() || null,
                displayDescription: v.displayDescription.trim() || null,
            })}
            fields={<HeaderFields />}
            view={
                <PropertyPanel>
                    <PropertyPanel.Field label="Title" help="Shown to guests above every step.">
                        {survey.displayTitle || "—"}
                    </PropertyPanel.Field>
                    <PropertyPanel.Field label="Description">{survey.displayDescription || "—"}</PropertyPanel.Field>
                </PropertyPanel>
            }
            onLive={onLive}
        />
    );
}

export function ThankYouSection({
    survey,
    onLive,
}: {
    survey: Survey;
    onLive: (values: SurveyDetailsValues | null) => void;
}) {
    return (
        <DetailsSection
            survey={survey}
            sectionId={SECTION.thanks}
            title="Thank you"
            pick={(v) => ({ thankYouText: v.thankYouText.trim() || null })}
            fields={<ThankYouFields />}
            view={
                <PropertyPanel>
                    <PropertyPanel.Field label="Thank-you message">{survey.thankYouText || "—"}</PropertyPanel.Field>
                </PropertyPanel>
            }
            onLive={onLive}
        />
    );
}
